import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const authHeaders = (token, tenantId) => ({
  'Authorization': `Bearer ${token}`,
  'X-Tenant-ID': tenantId,
  'Content-Type': 'application/json',
});

const fetchUsers = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/users?size=200`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.content ?? []);
};

const ROLE_META = {
  ADMIN:        { label: 'Admin',        color: '#4f46e5', bg: '#eef2ff' },
  DOCTOR:       { label: 'Doctor',       color: '#7c3aed', bg: '#f5f3ff' },
  PATIENT:      { label: 'Patient',      color: '#d97706', bg: '#fffbeb' },
  staff:        { label: 'Staff',        color: '#059669', bg: '#ecfdf5' },
  tenant_admin: { label: 'Tenant Admin', color: '#0891b2', bg: '#ecfeff' },
  system_admin: { label: 'System Admin', color: '#2563eb', bg: '#eff6ff' },
};

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || { label: role, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
      background: m.bg, color: m.color, border: `1px solid ${m.color}33`,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

const initials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

const SkeletonRow = () => (
  <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
    <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '35%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '50%', height: 11 }} />
    </div>
    <div className="skeleton" style={{ width: 72, height: 22, borderRadius: 9999 }} />
  </div>
);


const Users = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { tenant } = useTenant();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUsers(token, tenant?.id);
      setUsers(data);
    } catch (e) {
      setError(`Could not load users (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Users</span>
        </div>

        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h1 className="t-h1" style={{ marginBottom: 4 }}>👥 Users</h1>
              <p className="t-body-sm">
                {!loading && !error && `${users.length} user${users.length !== 1 ? 's' : ''} total`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="t-link"
                onClick={() => navigate('/admin/doctors')}
                style={{ padding: '8px 12px', border: '1px solid #7c3aed', borderRadius: 9999, background: '#f5f3ff', color: '#6d28d9' }}
              >
                + Add Doctor
              </button>
              <button
                className="t-link"
                onClick={() => navigate('/admin/patients')}
                style={{ padding: '8px 12px', border: '1px solid #d97706', borderRadius: 9999, background: '#fffbeb', color: '#b45309' }}
              >
                + Add Patient
              </button>
            </div>
          </div>
        </div>

        <div className="card fade-up-1" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div className="t-body-sm" style={{ marginBottom: 8, color: '#4b5563' }}>
            Use the buttons above to create doctor or patient accounts for the selected tenant.
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <div className="search-wrap" style={{ flex: '1 1 200px', minWidth: 180, marginBottom: 0 }}>
              <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
                placeholder="Search by name, email, phone…"
                style={{ height: 36, fontSize: 13 }}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
              )}
            </div>

            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="select-filter"
            >
              <option value="all">All roles</option>
              {Object.entries(ROLE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>

            {!loading && (
              <span className="t-meta" style={{ marginLeft: 'auto' }}>
                {filtered.length} of {users.length}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div className="card fade-up-2" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '10px 20px',
            background: '#f8f9ff', borderBottom: '1px solid #f3f4f6',
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif',
          }}>
            <span>User</span>
            <span>Role</span>
          </div>

          <div style={{ padding: '0 20px' }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : filtered.length === 0
                ? (
                  <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p className="empty-title">{search ? 'No users match' : 'No users found'}</p>
                  </div>
                )
                : filtered.map((u, idx) => {
                  const name = u.name || u.fullName || u.phone || 'Unknown';
                  return (
                    <div key={u.id || idx} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '14px 0', borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
                      animation: `fadeUp .3s ${idx * 20}ms ease both`,
                    }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${(ROLE_META[u.role] || {}).color || '#6366f1'}dd, ${(ROLE_META[u.role] || {}).color || '#6366f1'}88)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'Sora, sans-serif',
                      }}>
                        {initials(name)}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {name}
                        </div>
                        <div className="t-meta">
                          {u.email && <span>{u.email}</span>}
                          {u.phone && <span style={{ marginLeft: u.email ? 10 : 0 }}>📱 {u.phone}</span>}
                        </div>
                      </div>

                      <RoleBadge role={u.role} />
                    </div>
                  );
                })
            }
          </div>
        </div>

        {!loading && users.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <span className="t-meta">
              Showing {filtered.length}{filtered.length !== users.length ? ` of ${users.length}` : ''} user{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

      </main>
    </div>
  );
};

export default Users;